import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useContractList,
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUSES,
  type ContractStatus,
} from '@/hooks/useQuoteContract';
import { canSeeQuoteContractMenu } from '@/lib/quoteContractPermissions';

const BADGE: Record<ContractStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  signed: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  change_pending: 'bg-amber-100 text-amber-800',
  voided: 'bg-zinc-200 text-zinc-700',
};

export default function ContractListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const { rows, total, page, limit, loading, error, fetchContracts } =
    useContractList();
  const [filterStatus, setFilterStatus] = useState<ContractStatus | ''>('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProposal, setFilterProposal] = useState('');
  const [pageInput, setPageInput] = useState(1);

  useEffect(() => {
    if (!canRead) return;
    void fetchContracts({
      status: filterStatus || undefined,
      customerId: filterCustomer || undefined,
      proposalId: filterProposal || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [filterStatus, filterCustomer, filterProposal, pageInput, canRead, fetchContracts]);

  if (!canRead) return <AccessDenied resource="合同" />;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">合同管理</h1>
        <p className="text-sm text-muted-foreground">
          已生成、已签署、生效中、变更中的合同列表。
        </p>
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="status">状态</Label>
            <select
              id="status"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus((e.target.value as ContractStatus | '') || '')
              }
            >
              <option value="">全部</option>
              {CONTRACT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CONTRACT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="customer">客户 ID</Label>
            <Input
              id="customer"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="proposal">方案 ID</Label>
            <Input
              id="proposal"
              value={filterProposal}
              onChange={(e) => setFilterProposal(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          加载失败：{error.message}
        </Card>
      )}

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">合同号</th>
              <th className="px-3 py-2 text-left font-medium">标题</th>
              <th className="px-3 py-2 text-left font-medium">客户</th>
              <th className="px-3 py-2 text-left font-medium">关联报价</th>
              <th className="px-3 py-2 text-left font-medium">状态</th>
              <th className="px-3 py-2 text-right font-medium">合同金额</th>
              <th className="px-3 py-2 text-right font-medium">当前生效金额</th>
              <th className="px-3 py-2 text-left font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                  暂无合同
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono text-xs">{row.contractNo}</td>
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.customerId}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.quoteId}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${BADGE[row.status]}`}>
                      {CONTRACT_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {row.currency} {row.totalAmount}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {row.currency} {row.effectiveTotalAmount}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => navigate(`/quote-contract/contracts/${row.id}`)}
                    >
                      详情
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>共 {total} 条 · 第 {page} / {totalPages} 页</span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPageInput((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}
